import { useTranslate } from '@refinedev/core';
import { Typography, Tag, Table, Descriptions, Tooltip, Space } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import type {
  ContentScorecardOut,
  ContentScorecardCheckOut,
} from '@/api/generated/schemas';

const { Title } = Typography;

export const ProductScorecardTab = ({
  scorecard,
}: {
  scorecard: ContentScorecardOut;
}) => {
  const t = useTranslate();

  const sortedChecks = [...scorecard.checks].sort((a, b) => {
    if (a.passed !== b.passed) return a.passed ? 1 : -1;
    if (a.severity !== b.severity) return a.severity === 'error' ? -1 : 1;
    return a.code.localeCompare(b.code);
  });

  return (
    <>
      <Title level={4}>{t('content_catalog.products.tabs.scorecard')}</Title>

      <Descriptions
        size="small"
        column={{ xs: 1, sm: 2, md: 3 }}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item
          label={t('content_catalog.products.scorecard.displayable')}
        >
          <Tag
            color={scorecard.is_displayable ? 'success' : 'error'}
            icon={
              scorecard.is_displayable ? (
                <CheckCircleFilled />
              ) : (
                <CloseCircleFilled />
              )
            }
          >
            {scorecard.is_displayable ? t('common.yes') : t('common.no')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item
          label={t('content_catalog.products.scorecard.streamable')}
        >
          <Tag
            color={scorecard.is_streamable ? 'success' : 'error'}
            icon={
              scorecard.is_streamable ? (
                <CheckCircleFilled />
              ) : (
                <CloseCircleFilled />
              )
            }
          >
            {scorecard.is_streamable ? t('common.yes') : t('common.no')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item
          label={t('content_catalog.products.scorecard.fields.version')}
        >
          {scorecard.version}
        </Descriptions.Item>
      </Descriptions>

      <Table<ContentScorecardCheckOut>
        dataSource={sortedChecks}
        rowKey="code"
        pagination={false}
        size="small"
      >
        <Table.Column<ContentScorecardCheckOut>
          dataIndex="passed"
          title={t('content_catalog.products.scorecard.fields.passed')}
          render={(value: boolean) => (
            <Tooltip title={value ? t('common.yes') : t('common.no')}>
              {value ? (
                <CheckCircleFilled style={{ color: '#52c41a' }} />
              ) : (
                <CloseCircleFilled style={{ color: '#ff4d4f' }} />
              )}
            </Tooltip>
          )}
        />
        <Table.Column
          dataIndex="code"
          title={t('content_catalog.products.scorecard.fields.code')}
          render={(value: string) => (
            <Typography.Text code>{value}</Typography.Text>
          )}
        />
        <Table.Column
          dataIndex="severity"
          title={t('content_catalog.products.scorecard.fields.severity')}
          render={(value: string) => (
            <Typography.Text type="secondary">
              {t(`content_catalog.products.scorecard.severity.${value}`, {
                defaultValue: value,
              })}
            </Typography.Text>
          )}
        />
        <Table.Column
          dataIndex="gates"
          title={t('content_catalog.products.scorecard.fields.gates')}
          render={(value: string[]) => (
            <Space size={4} wrap>
              {value.map((gate) => (
                <Tag key={gate}>
                  {t(`content_catalog.products.scorecard.gates.${gate}`, {
                    defaultValue: gate,
                  })}
                </Tag>
              ))}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="message"
          title={t('content_catalog.products.scorecard.fields.message')}
          render={(value: string | null) => value ?? '-'}
        />
      </Table>
    </>
  );
};
